import React, { useCallback, useState } from "react";

import { Box, Button, Flex, Input, Text } from "@catchoftheday/cg-components";
import { isValidEmail, useIsMounted } from "@catchoftheday/utilities";

import { subscribeEmail } from "../../api";

type SubscribeInputProps = {
  onSubmit?: (arg0: string) => void;
};

export const SubscribeInput = ({ onSubmit }: SubscribeInputProps) => {
  const [email, setEmail] = useState("");
  const isMounted = useIsMounted();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [touched, setTouched] = useState(false);
  const showError = touched && error;

  const validateEmail = useCallback((email: string) => {
    setError(!isValidEmail(email));
  }, []);

  const handleChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      setEmail(e.currentTarget.value);
      validateEmail(e.currentTarget.value);
      setServerError(false);
    },
    [setEmail, validateEmail]
  );

  const handleBlur = useCallback(() => {
    setTouched(true);
    setServerError(false);
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (error || !isValidEmail(email)) return;
      setSubmitting(true);
      setServerError(false);

      try {
        await subscribeEmail(email);
        await onSubmit?.(email);
      } catch (e) {
        setServerError(true);
      }

      /** Don't update state unless component is still mounted to avoid react warnings */
      if (isMounted()) {
        setSubmitting(false);
      }
    },
    [email, error, isMounted, onSubmit]
  );

  return (
    <React.Fragment>
      <Flex as="form" width="100%" onSubmit={handleSubmit}>
        <Input
          fontWeight={100}
          fontSize={14}
          disabled={submitting}
          type="email"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Your e-mail address"
          height="54px"
          borderRadius="4px"
          borderTopRightRadius="0"
          borderBottomRightRadius="0"
          padding="11px"
          error={showError}
        />
        <Button
          onClick={() => {
            validateEmail(email);
            handleBlur();
          }}
          disabled={submitting || showError}
          type="submit"
          variant="secondary"
          borderBottomLeftRadius={0}
          borderTopLeftRadius={0}
          fontSize={14}
          height={54}
        >
          SUBSCRIBE
        </Button>
      </Flex>
      {showError ? (
        <Box>
          <Text color="error">Please enter a valid email address</Text>
        </Box>
      ) : null}
      {serverError ? (
        <Box>
          <Text color="error">Please try again</Text>
        </Box>
      ) : null}
    </React.Fragment>
  );
};
